import {Component, computed, inject, input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {NavigationEnd, Router} from '@angular/router';
import {toSignal} from '@angular/core/rxjs-interop';
import {of} from 'rxjs';
import {catchError, filter, map, startWith} from 'rxjs/operators';
import {FeatureFlagService} from '@app/services/feature-flag.service';
import {ConfigAPIService} from '@app/services/api/config-api.service';
import {AccountFeaturesEnum} from '@app/interfaces/config/accountFeaturesEnum';
import {CountryEnum} from '@app/enums/CountryEnum';

export type VersionToggleMode = 'legacy' | 'v2';

/**
 * URL patterns that use the `-legacy` suffix toggle scheme vs. the
 * `localStorage-only` toggle scheme.
 *
 * - `-legacy` scheme: for pages with a literal path component we can suffix
 *   (`/admin/vehicles/active` ↔ `/admin/vehicles/active-legacy`).
 * - localStorage scheme: for pages whose URLs end in a UUID or other
 *   variable segment (`/admin/vehicles/<uuid>`) — suffixing the UUID is
 *   fragile, so we flip a localStorage pref and reload to have the wrapper
 *   component switch components.
 */
const UUID_IN_VEHICLES = /^\/admin\/vehicles\/[0-9a-f]{8}-[0-9a-f]{4}/i;
const FORM_IN_VEHICLES = /^\/admin\/vehicles\/form(\/|\?|$)/i;
/**
 * List pages whose non-`-legacy` route renders a *wrapper* component that
 * picks v2 vs legacy based on the localStorage pref (see
 * `VehicleListPageWrapperComponent`, `VehicleArchivedListPageWrapperComponent`,
 * `VehicleImportedListPageWrapperComponent`). The URL stays the same in both
 * modes, so banner detection must consult localStorage instead of relying on
 * the `-legacy` suffix the way `create` does (which is a direct component, no
 * wrapper).
 */
// `ads` deliberately does NOT accept a trailing `/`: `/admin/vehicles/ads/...`
// are the per-vehicle ad form pages, which are not part of the migration.
const WRAPPED_LIST_IN_VEHICLES = /^\/admin\/vehicles\/(?:(?:active|archived|imported)(?:\/|\?|$)|ads(?:\?|$))/i;
/**
 * Path segments under `/admin/vehicles/` that have a v1 ↔ v2 twin route
 * registered with the `-legacy` suffix. When the URL's first vehicles-scoped
 * segment matches one of these we toggle by swapping the suffix on that
 * segment (not the last segment, which may be a UUID).
 */
const VEHICLES_TWIN_SEGMENTS = new Set([
  'active', 'archived', 'imported', 'form', 'ads',
]);

/**
 * Design 2.0 migration — drop-in banner that auto-detects whether the current
 * page is v2 or legacy, shows the appropriate copy, and lets the user flip
 * between the two.
 *
 * Detection:
 *   - URL has `-legacy` suffix on the last path segment, OR
 *   - URL is a UUID-suffixed detail page and localStorage pref says `false`
 *
 * Visibility is gated by:
 *   1. The account-level feature flag passed via `accountFlag` — if off, banner
 *      never shows.
 *   2. The `migratedRoutes` allowlist — only shows on pages that have a v2 and
 *      a legacy variant.
 *
 * Toggling:
 *   - `-legacy` scheme: append/remove `-legacy` suffix + navigate.
 *   - localStorage scheme: flip pref, hard reload (simplest way to remount
 *     the wrapper component which keyed its choice on ngOnInit).
 */
@Component({
  selector: 'app-version-toggle-banner',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './version-toggle-banner.component.html',
  styleUrls: ['./version-toggle-banner.component.scss'],
})
export class VersionToggleBannerComponent {
  readonly accountFlag = input.required<AccountFeaturesEnum>();
  readonly prefKey = input.required<string>();
  /**
   * Regex allowlist of URL patterns where the banner is relevant — pages that
   * have a v2 and a legacy variant. The banner stays hidden on every other
   * route.
   *
   * Default is empty so accidental omission fails closed (banner never shows).
   */
  readonly migratedRoutes = input<RegExp[]>([]);

  private readonly router = inject(Router);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly configApi = inject(ConfigAPIService);

  private readonly flags = toSignal(
    this.featureFlagService.getFeatureFlags().pipe(map(flagMap => flagMap ?? null)),
    {initialValue: null},
  );

  /**
   * BRIEFD-6962: Croatian accounts have no way back to legacy, so the toggle
   * itself is hidden for them. Hardcoded to country since this whole
   * banner/toggle mechanism is planned for removal once legacy is fully
   * retired (see BRIEFD-6962 for the tracking ticket).
   *
   * `catchError` matters here beyond the usual defensive-coding reason: once
   * the source errors, `toSignal` re-throws that error on every subsequent
   * read rather than falling back to `initialValue` — so a bare pipe would
   * make `isVisible` below throw during change detection instead of just
   * leaving the banner visible.
   */
  private readonly isCroatianAccount = toSignal(
    this.configApi.getAccountConfig().pipe(
      map(config => config?.countryCode === CountryEnum.CROATIA),
      catchError(() => of(false)),
    ),
    {initialValue: false},
  );

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter(ev => ev instanceof NavigationEnd),
      map(ev => (ev as NavigationEnd).urlAfterRedirects),
      startWith(this.router.url),
    ),
    {initialValue: this.router.url},
  );

  readonly mode = computed<VersionToggleMode>(() => {
    const url = this.currentUrl();
    if (/-legacy(?:\/|\?|$)/.test(url)) {
      return 'legacy';
    }
    if (UUID_IN_VEHICLES.test(url)) {
      // UUID detail page — localStorage is the source of truth.
      return localStorage.getItem(this.prefKey()) === 'false' ? 'legacy' : 'v2';
    }
    // Form page (`/admin/vehicles/form/<uuid>`) also obeys localStorage so the
    // banner stays in sync with the detail page it lives next to.
    if (FORM_IN_VEHICLES.test(url)) {
      return localStorage.getItem(this.prefKey()) === 'false' ? 'legacy' : 'v2';
    }
    // Wrapped list pages (`active|archived|imported`) — the route component is
    // a wrapper that decides v2 vs legacy from localStorage, so the URL alone
    // doesn't tell us which variant is rendered. Without this branch the
    // banner stuck on the "v2" copy after sidebar navigation brought the user
    // back to the plain URL while their pref was still legacy.
    if (WRAPPED_LIST_IN_VEHICLES.test(url)) {
      return localStorage.getItem(this.prefKey()) === 'false' ? 'legacy' : 'v2';
    }
    return 'v2';
  });

  readonly isVisible = computed(() => {
    const flags = this.flags();
    if (!flags || flags[this.accountFlag()] !== true) {
      return false;
    }
    // Per-account opt-out: legacy is retired for this dealer, so there is
    // nothing to toggle back to.
    if (flags[AccountFeaturesEnum.FEATURE_FLAG_AB_LEGACY_RETIRED] === true) {
      return false;
    }
    if (this.isCroatianAccount()) {
      return false;
    }
    const url = this.currentUrl();
    const routes = this.migratedRoutes();
    return routes.length > 0 && routes.some(pattern => pattern.test(url));
  });

  toggle(): void {
    const nextMode: VersionToggleMode = this.mode() === 'legacy' ? 'v2' : 'legacy';
    localStorage.setItem(this.prefKey(), String(nextMode === 'v2'));

    const url = this.currentUrl();
    if (UUID_IN_VEHICLES.test(url) || FORM_IN_VEHICLES.test(url)) {
      // localStorage scheme — force a reload so the wrapper re-reads the pref
      // on `ngOnInit` and switches the component. Used for routes where the
      // toggle-able segment is followed by a UUID we shouldn't touch.
      window.location.reload();
      return;
    }

    // `-legacy` suffix scheme — navigate to the twin URL within the same
    // lazy-loaded module. If the swap produces the same URL we're already on
    // (happens when a wrapped list page rendered the legacy variant via the
    // localStorage pref, so the URL had no `-legacy` suffix to flip), fall
    // back to a reload so the wrapper re-resolves its child component.
    const targetUrl = this.toggleUrl(nextMode);
    if (targetUrl === url) {
      window.location.reload();
      return;
    }
    this.router.navigateByUrl(targetUrl);
  }

  /**
   * Swap the `-legacy` suffix on the FIRST vehicles-scoped path segment, not
   * the last one. `/admin/vehicles/form/<uuid>` must toggle the `form`
   * segment (→ `/admin/vehicles/form-legacy/<uuid>`), never the trailing UUID.
   */
  private toggleUrl(nextMode: VersionToggleMode): string {
    const current = this.currentUrl();
    const vehiclesIdx = current.indexOf('/admin/vehicles/');
    if (vehiclesIdx === -1) {
      return current;
    }
    const head = current.slice(0, vehiclesIdx + '/admin/vehicles/'.length);
    const tail = current.slice(vehiclesIdx + '/admin/vehicles/'.length);
    // Split off the first segment from the rest (`/...` or `?...` or end).
    const segMatch = tail.match(/^([^\/?]+)([\/?].*)?$/);
    if (!segMatch) {
      return current;
    }
    const segment = segMatch[1];
    const rest = segMatch[2] ?? '';
    const baseSegment = segment.endsWith('-legacy') ? segment.slice(0, -'-legacy'.length) : segment;
    if (!VEHICLES_TWIN_SEGMENTS.has(baseSegment)) {
      return current;
    }
    const nextSegment = nextMode === 'legacy' ? `${baseSegment}-legacy` : baseSegment;
    return `${head}${nextSegment}${rest}`;
  }
}
